/**
 * @module userProfile
 */
import { Record } from 'immutable';
import { User } from './user';
import { apiPost } from './api';
import { userProfileServiceUrl } from './const';
import { LogEntries, NotificationsSetting } from './models';

/**
 * Return the notifications setting for the user.
 * https://github.com/severalnines/bar-user-profile-api#post-notification-settingsget
 */
export async function getNotificationsSetting() {
    const res = await apiPost(
        `${userProfileServiceUrl}/notification-settings/get`,
        null
    );
    return new NotificationsSetting(res);
}

/**
 * Update the notifications setting for the user and return the new notifications setting.
 * https://github.com/severalnines/bar-user-profile-api#post-notification-settingsset
 * @param {NotificationsSetting} settings
 * @returns {NotificationsSetting}
 */
export async function updateNotificationsSetting({
    sendEventBackupCreated,
    sendEventBackupFailed,
    sendEventBackupPurged,
    sendEventBackupWithErrors,
    sendEventScheduleCreated,
    sendEventScheduleRemoved,
    sendEventSchedulePaused,
    sendEventScheduleResumed,
    sendEventAgentInstalled,
    sendEventAgentRemoved,
    sendEventAgentErrored,
    sendEventAgentStarted,
    sendEventAgentStopped,
}) {
    const res = await apiPost(
        `${userProfileServiceUrl}/notification-settings/set`,
        {
            sendEventBackupCreated,
            sendEventBackupFailed,
            sendEventBackupPurged,
            sendEventBackupWithErrors,
            sendEventScheduleCreated,
            sendEventScheduleRemoved,
            sendEventSchedulePaused,
            sendEventScheduleResumed,
            sendEventAgentInstalled,
            sendEventAgentRemoved,
            sendEventAgentErrored,
            sendEventAgentStarted,
            sendEventAgentStopped,
        }
    );
    return new NotificationsSetting(res);
}

/**
 * Subscribe to newsletter
 * https://github.com/severalnines/bar-user-profile-api#post-usersubscribe
 * @returns {promise}
 */
export async function subscribe() {
    return await apiPost(`${userProfileServiceUrl}/user/subscribe`, {});
}

/**
 * Unsubscribe to newsletter
 * https://github.com/severalnines/bar-user-profile-api#post-userunsubscribe
 * @returns {promise}
 */
export async function unsubscribe() {
    return await apiPost(`${userProfileServiceUrl}/user/unsubscribe`, {});
}

/**
 * Resend email verification.
 * https://github.com/severalnines/bar-user-profile-api#post-userresendverificationemail
 * @param {string} url
 * @returns {promise}
 */
export async function resendVerificationEmail(
    url = `${userProfileServiceUrl}/user/resendVerificationEmail`
) {
    return await apiPost(url, {});
}

/**
 * Delete a user. In fact, it will just send a request to delete the user (at least for now).
 * https://github.com/severalnines/bar-user-profile-api#post-userdelete
 * @returns {promise}
 */
export async function userDelete() {
    return await apiPost(`${userProfileServiceUrl}/user/delete`, {});
}

/**
 * Update user name
 * https://github.com/severalnines/bar-user-profile-api#post-userupdatename
 * @param {string} firstName
 * @param {string} lastName
 * @returns {User}
 */
export async function nameUpdate({ firstName, lastName }) {
    const res = await apiPost(`${userProfileServiceUrl}/user/updateName`, {
        firstName,
        lastName,
    });
    return new User(res);
}

/**
 * Update account password
 * https://github.com/severalnines/bar-user-profile-api#post-userupdatepassword
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {promise}
 */
export async function passwordUpdate({ currentPassword, newPassword }) {
    return await apiPost(`${userProfileServiceUrl}/user/updatePassword`, {
        currentPassword,
        newPassword,
    });
}

/**
 * Add a new contact for the user in the session.
 * https://github.com/severalnines/bar-user-profile-api#post-contactscreate
 * @param {number} type [1:phone, 2:email]
 * @param {string} value
 * @returns {Contact}
 */
export async function contactsCreate({ type, value }) {
    const res = await apiPost(`${userProfileServiceUrl}/contacts/create`, {
        type,
        value,
    });
    return new Contact(res);
}

/**
 * Delete a contact
 * https://github.com/severalnines/bar-user-profile-api#post-contactsdelete
 * @param {string} id
 * @returns {promise}
 */
export async function contactsDelete({ id }) {
    return await apiPost(`${userProfileServiceUrl}/contacts/delete`, {
        id,
    });
}

/**
 * Retrieve the contacts list for the user in the session.
 * https://github.com/severalnines/bar-user-profile-api#post-contactslist
 */
export async function contactsList() {
    const res = await apiPost(`${userProfileServiceUrl}/contacts/list`, null);
    return res.map((c) => new Contact(c));
}

/**
 * Retrieve the audit log for user.
 * @param {number} page
 * @param {number} perPage
 * @returns {Promise<LogEntries>}
 */
export async function userAuditLogList(page = 1, perPage = 100) {
    const res = await apiPost(`${userProfileServiceUrl}/audit-log/user`, {
        page,
        perPage,
    });
    return new LogEntries(res);
}

/**
 * @name feedbackSent
 * @desc Confirms user has sent the feedback.
 * @returns {Promise}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function feedbackSent() {
    const res = await apiPost(`${userProfileServiceUrl}/user/feedbackSent`);
    return res;
}

/**
 * @name sendFeedback
 * @desc Sends user feedback.
 * @returns {Promise}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function sendFeedback({ origin, topic, message }) {
    const res = await apiPost(`${userProfileServiceUrl}/user/sendFeedback`, {
        origin,
        topic,
        message,
    });
    return res;
}

/**
 * @class Contact
 * @classdesc Contact model
 */
export class Contact extends Record({
    id: null,
    userId: null,
    type: null,
    value: null,
}) {}

/**
 * @class ContactType
 * @classdesc ContactType model
 */
export class ContactType extends Record({
    id: null,
    constant: null,
    description: null,
}) {}
